import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Row,
  Col,
  Statistic,
  Spin,
  message,
  Button,
  Space,
  Typography,
  DatePicker,
  Select,
  Input
} from 'antd';
import {
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined
} from '@ant-design/icons';
import apiClient from '../utils/apiClient';
import moment from 'moment';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

function PaymentHistory() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    dateRange: null,
    status: null,
    search: ''
  });

  useEffect(() => {
    fetchTransactions();
  }, [pagination.current, pagination.pageSize, filters]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = {
        limit: pagination.pageSize,
        offset: (pagination.current - 1) * pagination.pageSize
      };

      if (filters.dateRange && filters.dateRange.length === 2) {
        params.start_date = filters.dateRange[0].format('YYYY-MM-DD');
        params.end_date = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await apiClient.get('/api/payment-scanner/processed-transactions', { params });
      
      setTransactions(response.data.transactions || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || 0
      }));
    } catch (error) {
      message.error('Unable to load payment history');
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(price);
  };

  const formatDate = (dateString) => {
    return moment(dateString).format('DD/MM/YYYY HH:mm:ss');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processed': return 'green';
      case 'failed': return 'red';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'processed': return 'Processed';
      case 'failed': return 'Failed';
      default: return 'Unknown';
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Transaction ID',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
      render: (text) => (
        <Text code style={{ fontSize: '12px' }}>
          {text}
        </Text>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <Text strong style={{ color: '#52c41a', fontSize: '16px' }}>
          {formatPrice(amount)}
        </Text>
      ),
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: 'Reference',
      dataIndex: 'content',
      key: 'content',
      render: (content) => (
        <Text style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {content}
        </Text>
      ),
    },
    {
      title: 'Plan',
      dataIndex: 'plan_id',
      key: 'plan_id',
      render: (planId) => (
        <Tag color="blue">
          Plan #{planId}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Time',
      dataIndex: 'processed_at',
      key: 'processed_at',
      render: (date) => formatDate(date),
      sorter: (a, b) => new Date(a.processed_at) - new Date(b.processed_at),
    },
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  const handleRefresh = () => {
    fetchTransactions();
    message.success('Data refreshed');
  };

  const totalAmount = transactions.reduce((sum, t) => sum + t.amount, 0);
  const processedCount = transactions.filter(t => t.status === 'processed').length;
  const failedCount = transactions.filter(t => t.status === 'failed').length;

  return (
    <div>
      <div className="page-header">
        <Title level={2}>
          <Space>
            <DollarOutlined />
            <span>Payment History</span>
          </Space>
        </Title>
        <Text type="secondary">Track transactions that have been automatically processed</Text>
      </div>

      {/* Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total Transactions"
              value={pagination.total}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Processed"
              value={processedCount}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Failed"
              value={failedCount}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total Amount"
              value={totalAmount}
              formatter={(value) => formatPrice(value)}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>Date Range:</Text>
              <RangePicker
                style={{ width: '100%' }}
                value={filters.dateRange}
                onChange={(dates) => handleFilterChange('dateRange', dates)}
                format="DD/MM/YYYY"
              />
            </Space>
          </Col>
          <Col xs={24} sm={8}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>Status:</Text>
              <Select
                style={{ width: '100%' }}
                placeholder="All statuses"
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                allowClear
              >
                <Option value="processed">Processed</Option>
                <Option value="failed">Failed</Option>
              </Select>
            </Space>
          </Col>
          <Col xs={24} sm={8}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>Search:</Text>
              <Input
                placeholder="Transaction ID, reference..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                prefix={<SearchOutlined />}
              />
            </Space>
          </Col>
        </Row>
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Space>
            <Button
              icon={<FilterOutlined />}
              onClick={() => {
                setFilters({
                  dateRange: null,
                  status: null,
                  search: ''
                });
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
            >
              Clear Filters
            </Button>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        </div>
      </Card>

      {/* Transactions Table */}
      <Card>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={transactions}
            rowKey="id"
            loading={loading}
            scroll={{ x: 800 }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} transactions`,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setPagination(prev => ({ ...prev, current: page, pageSize }));
              },
              onShowSizeChange: (current, size) => {
                setPagination(prev => ({ ...prev, current: 1, pageSize: size }));
              }
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}

export default PaymentHistory;